function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

export function notificationsSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  )
}

export async function fetchVapidPublicKey(): Promise<string> {
  const response = await fetch('/api/push/vapid-public-key', {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error('Push notifications are not configured')
  const body: unknown = await response.json()
  const key =
    body && typeof body === 'object' && 'publicKey' in body
      ? (body as { publicKey?: unknown }).publicKey
      : null
  if (typeof key !== 'string' || !key) throw new Error('Push notifications are not configured')
  return key
}

/**
 * Requests notification permission only when still in the default state,
 * then registers the service worker and creates a push subscription.
 */
export async function enablePushSubscription(): Promise<PushSubscriptionJSON> {
  if (!notificationsSupported()) {
    throw new Error('Notifications are not supported in this browser')
  }

  if (Notification.permission === 'denied') {
    throw new Error('Notifications are blocked in your browser settings')
  }

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted')
    }
  }

  const registration = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready

  const publicKey = await fetchVapidPublicKey()
  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    })
  }

  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Could not create a push subscription')
  }
  return json
}

export async function enableReportNotifications(input: {
  kind: 'sighting' | 'lost'
  id: number
}): Promise<void> {
  const subscription = await enablePushSubscription()
  const response = await fetch('/api/notifications/enable', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      kind: input.kind,
      id: input.id,
      subscription: {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: subscription.keys!.p256dh,
          auth: subscription.keys!.auth,
        },
      },
    }),
  })
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    const message =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : 'Could not enable notifications'
    throw new Error(message)
  }
}

/**
 * After creating a report: show the opt-in UI only when the browser permission
 * is still undecided (never enabled). If already granted, silently turn on
 * notifications for this report. Denied/unsupported → skip without prompting.
 */
export async function resolvePostCreateNotifications(input: {
  kind: 'sighting' | 'lost'
  id: number
}): Promise<'prompt' | 'done'> {
  if (!notificationsSupported()) return 'done'
  if (Notification.permission === 'denied') return 'done'
  if (Notification.permission === 'default') return 'prompt'

  try {
    await enableReportNotifications(input)
  } catch {
    // Permission was already granted; don't block closing the form if refresh fails.
  }
  return 'done'
}

export async function sendReportMessage(input: {
  kind: 'sighting' | 'lost'
  id: number
  body: string
}): Promise<void> {
  const response = await fetch('/api/messages', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    const message =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : 'Could not send message'
    throw new Error(message)
  }
}
